import { useCallback, useState, useEffect, ChangeEvent } from 'react';
import { useWriteContract, useConfig, useAccount } from 'wagmi';
import { getAddress, type Hex } from 'viem';
import { useEnsureChain } from '../../../hooks/useEnsureChain';
import { dexAbi } from 'sc-interfaces';
import { useTransactionState } from '../../../hooks/useTransactionMonitor';
import useAppStore, { useActiveExchangeAddress } from '../../../store/rootStore.ts';
import { useHeartbeatStore } from '@/common/heartbeatStore.ts';
import { useAccountErrorHandler, accountReasonHandler } from '../../../hooks/useErrorHandler';
import { createRevertDecodingParams } from '../../../utils/decodeScError';
import { prepareContractWrite } from '../../../utils/contractWriteHelpers';
import { useWalletNonce } from '../../../hooks/useWalletNonce';

export function useOneClickTrading() {
  const config = useConfig();
  const { address: accountAddress } = useAccount();
  const { getNonce } = useWalletNonce();
  const { ensureChain, configChainId, isSwitching } = useEnsureChain();
  const exchangeAddress = useActiveExchangeAddress();
  const isOneClickEnabled = useAppStore((state) => state.account?.fw);
  const isIndexingLagging = useHeartbeatStore((state) => state.trading.indexingStatus === 'far_behind');

  const { handleError: handleOneClickError } = useAccountErrorHandler('one-click trading setting');

  const [expectedState, setExpectedState] = useState<boolean | null>(null);
  const [transactionData, setTransactionData] = useState<Hex | undefined>(undefined);

  const {
    writeContractAsync,
    isPending: isWritePending,
    isSuccess: isWriteSuccess,
    isError: isWriteError,
    data: txHash,
  } = useWriteContract();

  const { isPending: isTransactionPending, isError: isTransactionError } = useTransactionState({
    isWritePending,
    isWriteSuccess,
    isWriteError,
    txHash,
    messages: {
      confirming: 'Updating one-click trading setting...',
      confirmed: 'One-click trading setting updated successfully',
      failed: 'Failed to update one-click trading setting',
      error: 'An error occurred while updating the setting',
      rejected: 'Transaction was rejected',
    },
    onSuccess: () => {
      // we keep waiting for update through ws
    },
    revertDecoding: createRevertDecodingParams(exchangeAddress, transactionData, accountAddress),
    reasonHandler: accountReasonHandler,
  });

  useEffect(() => {
    if (expectedState != null && isOneClickEnabled === expectedState) {
      setExpectedState(null);
      setTransactionData(undefined);
    }
  }, [isOneClickEnabled, expectedState]);

  useEffect(() => {
    if (isWriteError || isTransactionError) {
      setExpectedState(null);
      setTransactionData(undefined);
    }
  }, [isWriteError, isTransactionError]);

  const toggleOneClickTrading = useCallback(
    async (enable: boolean | ChangeEvent<HTMLInputElement>) => {
      const value = typeof enable === 'boolean' ? enable : enable.target.checked;
      setExpectedState(value);

      try {
        await ensureChain();
      } catch (error) {
        handleOneClickError(error);
        setExpectedState(null);
        return;
      }

      try {
        const prepared = await prepareContractWrite({
          config,
          address: exchangeAddress!,
          abi: dexAbi,
          functionName: 'allowOrderForwarding',
          args: [value] as const,
          account: accountAddress,
          chainId: configChainId,
        });

        setTransactionData(prepared.transactionData);

        const nonce = await getNonce();

        await writeContractAsync({
          ...(prepared.writeArgs as any),
          abi: dexAbi,
          functionName: 'allowOrderForwarding',
          nonce,
        });
      } catch (error) {
        handleOneClickError(error);
        setExpectedState(null);
        setTransactionData(undefined);
      }
    },
    [writeContractAsync, exchangeAddress, config, accountAddress, handleOneClickError, getNonce, ensureChain, configChainId]
  );

  const isPending =
    isWritePending || isTransactionPending || isSwitching || (expectedState != null && isOneClickEnabled !== expectedState);

  const isAuthorized = useAppStore((state) => state.status === 'AUTHORIZED');

  return {
    isOneClickEnabled: isOneClickEnabled && !isIndexingLagging,
    handleToggleChange: toggleOneClickTrading,
    isPending,
    isAuthorized,
  };
}
